import Image from "next/image";
import type { JSX } from "react";

import { cn } from "@/lib/utils";

type BrandLogoVariant = "dark" | "light";

interface BrandLogoProps {
  variant?: BrandLogoVariant;
  className?: string;
  imageClassName?: string;
}

const WRAPPER_CLASSES: Record<BrandLogoVariant, string> = {
  dark: "bg-neutral-900 text-white",
  light: "bg-white text-neutral-900",
};

const IMAGE_FILTER: Record<BrandLogoVariant, string> = {
  dark: "",
  light: "invert",
};

interface BrandLogoProps {
  variant?: BrandLogoVariant;
  className?: string;
  imageClassName?: string;
  layoutVariant?: "default" | "vertical";
  showText?: boolean;
}

export function BrandLogo({
  variant = "dark",
  className,
  imageClassName,
  layoutVariant = "default",
  showText = true,
}: BrandLogoProps): JSX.Element {
  const defaultImageElement = (
    <Image
      alt="Isotipo Vivi el Movimiento"
      src="/images/logo.png"
      width={56}
      height={56}
      priority
      className={cn(
        "h-11 w-11 sm:h-12 sm:w-12 object-contain",
        IMAGE_FILTER[variant],
        imageClassName,
      )}
    />
  );

  const defaultTextElement = (
    // Cooper Hewitt itálica: "VIVI EL" en Medium (500) y "MOVIMIENTO" en Bold
    // (700), que son los dos únicos archivos cargados.
    <span className="flex items-baseline whitespace-nowrap text-center font-wordmark text-[0.65rem] italic uppercase leading-none sm:text-sm">
      <span className="font-medium tracking-[0.15em] sm:tracking-[0.18em]">VIVI EL</span>
      <span className="ml-1.5 sm:ml-3 font-bold tracking-[0.42em] sm:tracking-[0.62em]">
        MOVIMIENTO
      </span>
    </span>
  );

  if (layoutVariant === "vertical") {
    const verticalImageElement = (
      <Image
        alt="Isotipo Vivi el Movimiento"
        src="/images/logo.png"
        width={56}
        height={56}
        priority
        className={cn("h-12 w-12 object-contain", IMAGE_FILTER[variant], imageClassName)}
      />
    );

    const verticalTextElement = (
      <span className="flex flex-col items-center whitespace-nowrap font-wordmark text-xs italic uppercase leading-none sm:text-sm">
        <span className="font-medium tracking-[0.18em]">VIVI EL </span>
        <span className="font-bold tracking-[0.62em]">MOVIMIENTO</span>
      </span>
    );

    const baseWrapper = cn("shadow-sm border-2 border-primary", WRAPPER_CLASSES[variant]);
    const circleWrapper = cn(
      "flex h-16 w-16 items-center justify-center rounded-full",
      baseWrapper,
    );
    const pillWrapper = cn("flex flex-col items-center gap-1 rounded-3xl px-2 py-3", baseWrapper);

    return (
      <div className={cn(showText ? pillWrapper : circleWrapper, className)}>
        {verticalImageElement}
        {showText ? verticalTextElement : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-3xl px-3 py-2.5 shadow-sm border-2 border-primary sm:gap-4 sm:px-5 sm:py-3",
        WRAPPER_CLASSES[variant],
        className,
      )}
    >
      {defaultImageElement}
      {showText ? defaultTextElement : null}
    </div>
  );
}
